import { Stack } from '@mui/material'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium'
import { TFunction } from 'i18next'
import { EnqueueSnackbar } from 'notistack'
import { AssigneeEntity } from '@omnea-app/core/blocks'
import { ProfileBox } from '@omnea-app/fe-users-and-groups/ProfileBox'
import { CounterChip } from '@omnea-app/frontend/components/shared/ui/chip/CounterChip'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { RowGroupingColumnTableCell } from '../../row-grouping/components/RowGroupingColumnTableCell'
import { ROW_GROUPING_NONE_GROUPING_TEXT_VALUE } from '../../row-grouping/helpers'
import { BaseColumnConfig } from '../types'
import { baseColumnConfig } from './baseColumnConfig'
import type { GridValidRowModel } from '@mui/x-data-grid/models/gridRows'

type Dependencies = {
  tCommon: TFunction<'common'>
  enqueueSnackbar: EnqueueSnackbar
}

export type AssigneesColumnConfig<T extends GridValidRowModel> = BaseColumnConfig<T, 'assignees'> & {
  columnType: 'assignees'
  getAssignees: (params: GridRenderCellParams<T>) => AssigneeEntity[] | undefined
  valueGetter: GridColDef<T>['valueGetter']
  groupingValueGetter?: GridColDef<T>['groupingValueGetter']
}

export const getAssigneesColumn = <T extends GridValidRowModel>({
  dependencies: { tCommon, enqueueSnackbar },
  style,
  ...config
}: AssigneesColumnConfig<T> & { dependencies: Dependencies }): GridColDef<T> => ({
  ...baseColumnConfig,
  ...style,
  ...config,
  groupingValueGetter: config.groupingValueGetter ?? config.valueGetter,
  renderCell: (params) => {
    if (!isDefined(params.value)) return null
    if (params.rowNode.type === 'group') {
      return (
        <RowGroupingColumnTableCell
          columnHeaderName={config.headerName}
          value={params.value || ROW_GROUPING_NONE_GROUPING_TEXT_VALUE}
          id={params.id}
        />
      )
    }
    const assignees = config.getAssignees(params) ?? []
    const [firstAssignee, ...otherAssignees] = assignees
    if (!firstAssignee) return null
    const name = firstAssignee.name ?? firstAssignee.email ?? ''
    const text = firstAssignee.email ?? name

    return (
      <Stack
        direction="row"
        alignItems="center"
        height="100%"
        {...(config?.getDataTestId ? { ['data-testid']: config.getDataTestId(params) } : {})}
      >
        <ProfileBox
          text={name}
          tooltipLabel={tCommon('table.copy', { text })}
          avatarVariant="circular"
          userId={firstAssignee.id}
          isArchived={firstAssignee.isArchived}
          onClick={async (e) => {
            e.preventDefault()
            await navigator.clipboard.writeText(text)
            enqueueSnackbar(tCommon('table.copy-snackbar', { text }), { variant: 'success' })
          }}
        />
        {otherAssignees.length > 0 && (
          <span style={{ marginLeft: 8 }}>
            <CounterChip count={otherAssignees.length} hint={otherAssignees.map(({ name }) => name).join(', ')} />
          </span>
        )}
      </Stack>
    )
  },
})
